import type {
	ColDef,
	ColumnState,
	IRowNode,
	PostSortRowsParams,
	RowDataUpdatedEvent,
	SortChangedEvent,
} from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { DataGrid, tokens } from '@treams/treams-ui';

import type { ScoreModuleStatus, TreamsScoreRow } from '../TreamsScore.types';
import {
	type ActiveScoreSort,
	BOTTOM_DIVIDER_ID,
	DividerFullWidthRow,
	type GridRow,
	HIDDEN_DIVIDER_ID,
	HiddenTeamsDividerRow,
	type ScoreColors,
	type ScoreColumnId,
	type ScoreColumnLabels,
	type ScoreSort,
	TOP_DIVIDER_ID,
	getEdgeCount,
	isDivider,
	selectCompactTeams,
} from './cells';
import { buildColumnDefs } from './columns';

// Width the widest name may take up before it gets truncated, plus padding
// added on top for the truncated names.
const NAME_MAX_WIDTH = 320;
const NAME_PADDING = 30;

// Measures the widest team name with the grid's font (15px, inherited family)
// so the name column hugs its content instead of flexing.
const widestNameWidth = (names: string[]): number => {
	const context = document.createElement('canvas').getContext('2d');
	if (!context) return NAME_MAX_WIDTH;

	context.font = `400 15px ${getComputedStyle(document.body).fontFamily}`;

	return Math.ceil(Math.max(0, ...names.map((name) => context.measureText(name).width)));
};

// Stable identity: the table re-renders on sort changes (activeSort state), and
// a fresh defaultColDef object would make AG Grid re-apply the column defs —
// including the initial `sort: 'desc'` — undoing the user's sort.
const DEFAULT_COL_DEF = { sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[] };

type TreamsScoreTableProps = {
	teams: TreamsScoreRow[];
	columnLabels: ScoreColumnLabels;
	moduleStatus: ScoreModuleStatus;
	getScoreColors?: (score: number) => ScoreColors;
	compact?: boolean;
	nameColumnHeader?: string;
	showManagerColumn?: boolean;
	emptyState?: {
		icon?: ReactNode;
		title?: string;
		description?: string;
	};
};

export function TreamsScoreTable({
	teams,
	columnLabels,
	moduleStatus,
	getScoreColors,
	compact,
	nameColumnHeader,
	showManagerColumn,
	emptyState,
}: TreamsScoreTableProps) {
	const { t } = useTranslation();

	const edgeCount = getEdgeCount(teams.length);
	const effectiveCompact = !!compact && edgeCount > 0 && teams.length > edgeCount * 2;

	// null = non-score sort (name/manager): compact subset is score-based, so
	// drop it and show the full list instead of a stale edge slice.
	const [activeSort, setActiveSort] = useState<ActiveScoreSort | null>({
		colId: 'total',
		direction: 'desc',
	});

	const rowData = useMemo<GridRow[]>(() => {
		if (effectiveCompact && activeSort) {
			return [
				...selectCompactTeams(teams, edgeCount, activeSort),
				{ id: HIDDEN_DIVIDER_ID, divider: true },
			];
		}

		if (edgeCount === 0 || effectiveCompact) return [...teams];

		return [
			...teams,
			{ id: TOP_DIVIDER_ID, divider: true },
			{ id: BOTTOM_DIVIDER_ID, divider: true },
		];
	}, [teams, edgeCount, effectiveCompact, activeSort]);

	const getScoreSort = useCallback(
		(columnState: ColumnState[]): ScoreSort | null => {
			const sortedColumn = columnState.find((col) => col.sort);
			if (!sortedColumn?.sort) return null;

			const columnLabel = columnLabels[sortedColumn.colId as ScoreColumnId];
			if (!columnLabel) return null;

			return {
				direction: sortedColumn.sort,
				label: columnLabel,
			};
		},
		[columnLabels],
	);

	const repositionDividers = useCallback(
		(params: PostSortRowsParams<GridRow>) => {
			const { nodes, api } = params;

			const teamNodes = nodes.filter((node) => !isDivider(node.data));
			const ordered = [...teamNodes];

			if (effectiveCompact) {
				const hiddenNode = nodes.find((node) => node.data?.id === HIDDEN_DIVIDER_ID);

				if (hiddenNode && getScoreSort(api.getColumnState()) && teamNodes.length > edgeCount) {
					ordered.splice(edgeCount, 0, hiddenNode);
				}
			} else if (edgeCount > 0 && getScoreSort(api.getColumnState())) {
				const topNode = nodes.find((node) => node.data?.id === TOP_DIVIDER_ID);
				const bottomNode = nodes.find((node) => node.data?.id === BOTTOM_DIVIDER_ID);

				if (bottomNode && teamNodes.length > edgeCount * 2) {
					ordered.splice(teamNodes.length - edgeCount, 0, bottomNode);
				}

				if (topNode && teamNodes.length > edgeCount) {
					ordered.splice(edgeCount, 0, topNode);
				}
			}

			nodes.length = 0;
			nodes.push(...ordered);
		},
		[getScoreSort, edgeCount, effectiveCompact],
	);

	// With getRowId the grid applies data changes as update transactions and
	// keeps the existing row order; reapply the active sort so recalculated
	// scores (e.g. launch score-params tweaks) resort the table live.
	const onRowDataUpdated = useCallback((event: RowDataUpdatedEvent<GridRow>) => {
		event.api.refreshClientSideRowModel('sort');
	}, []);

	const onSortChanged = useCallback(
		(event: SortChangedEvent<GridRow>) => {
			const sortedColumn = event.api.getColumnState().find((col) => col.sort);
			const nextSort: ActiveScoreSort | null =
				sortedColumn?.sort && columnLabels[sortedColumn.colId as ScoreColumnId]
					? { colId: sortedColumn.colId as ScoreColumnId, direction: sortedColumn.sort }
					: null;

			// Flushed synchronously: in compact mode rowData is derived from
			// activeSort, and an async render would paint a frame of the previous
			// column's edge teams reordered under the new sort before the correct
			// subset lands.
			if (activeSort?.colId !== nextSort?.colId || activeSort?.direction !== nextSort?.direction) {
				flushSync(() => setActiveSort(nextSort));
			}

			const dividerNodes: IRowNode<GridRow>[] = [];

			event.api.forEachNode((node) => {
				if (isDivider(node.data)) dividerNodes.push(node);
			});

			event.api.redrawRows({ rowNodes: dividerNodes });
		},
		[columnLabels, activeSort],
	);

	const hiddenCount = teams.length - edgeCount * 2;

	const fullWidthCellRenderer = useCallback(
		(props: CustomCellRendererProps<GridRow>) =>
			props.data?.id === HIDDEN_DIVIDER_ID ? (
				<HiddenTeamsDividerRow {...props} getScoreSort={getScoreSort} hiddenCount={hiddenCount} />
			) : (
				<DividerFullWidthRow {...props} getScoreSort={getScoreSort} edgeCount={edgeCount} />
			),
		[getScoreSort, edgeCount, hiddenCount],
	);

	const nameColumnWidth = useMemo(
		() => Math.min(widestNameWidth(teams.map((team) => team.name)), NAME_MAX_WIDTH) + NAME_PADDING,
		[teams],
	);

	const columnDefs = useMemo<ColDef<GridRow>[]>(
		() =>
			buildColumnDefs({
				columnLabels,
				getScoreColors,
				moduleStatus,
				nameColumnHeader,
				nameColumnWidth,
				showManagerColumn,
				t,
			}),
		[
			columnLabels,
			getScoreColors,
			moduleStatus,
			nameColumnHeader,
			nameColumnWidth,
			showManagerColumn,
			t,
		],
	);

	return (
		<Box mt={3} px={0}>
			<DataGrid<GridRow>
				rowData={rowData}
				columnDefs={columnDefs}
				defaultColDef={DEFAULT_COL_DEF}
				getRowId={({ data }) => data.id}
				rowHeight={56}
				postSortRows={repositionDividers}
				onRowDataUpdated={onRowDataUpdated}
				onSortChanged={onSortChanged}
				isFullWidthRow={({ rowNode }) => isDivider(rowNode.data)}
				fullWidthCellRenderer={fullWidthCellRenderer}
				getRowHeight={({ data }) => (isDivider(data) ? 56 : undefined)}
				getRowStyle={({ data }) =>
					isDivider(data) ? { background: tokens.surface.primary } : undefined
				}
				emptyState={emptyState}
			/>
		</Box>
	);
}
